import React, { useState } from 'react';

// ── Layout constants ─────────────────────────────
const W = 980;
const H = 660;
const CX = 490;   // center x
const CY = 335;   // center y
const R  = 280;   // orbit radius

// ── Template ─────────────────────────────────────
const TEMPLATE = {
  x: CX, y: CY,
  steps: ['Requirements', 'Capacity Est.', 'API Design', 'High-Level Design', 'Deep Dive'],
};

// ── 10 problems at 36° each (0° = top, clockwise) ─
// angle(°) → x = CX + R·sin(a), y = CY − R·cos(a)
const PROBLEMS = [
  {
    id: 'instagram', label: 'Instagram Newsfeed', lines: ['Instagram', 'Newsfeed'],
    x: 403, y: 69,   // 342°
    color: '#a78bfa', cluster: 'Social & Feed',       difficulty: 'Medium', emoji: '📱',
    concepts: ['Fan-out on Write', 'CDN', 'Redis Caching', 'Pre-signed URLs'],
  },
  {
    id: 'youtube', label: 'YouTube / Netflix', lines: ['YouTube /', 'Netflix'],
    x: 577, y: 69,   // 18°
    color: '#60a5fa', cluster: 'Streaming & Media',   difficulty: 'Hard',   emoji: '🎬',
    concepts: ['HLS Encoding', 'CDN', 'Chunked Streaming', 'Workflow Engine'],
  },
  {
    id: 'tinyurl', label: 'TinyURL', lines: ['TinyURL'],
    x: 716, y: 170,  // 54°
    color: '#fbbf24', cluster: 'Infra & Scale',       difficulty: 'Easy',   emoji: '🔗',
    concepts: ['Base62 Encoding', 'Zookeeper', 'Counter Shards', 'Collision Handling'],
  },
  {
    id: 'ratelimiter', label: 'Rate Limiter', lines: ['Rate', 'Limiter'],
    x: 770, y: 335,  // 90°
    color: '#fbbf24', cluster: 'Infra & Scale',       difficulty: 'Medium', emoji: '⚡',
    concepts: ['Token Bucket', 'Sliding Window', 'Redis', 'Distributed Sync'],
  },
  {
    id: 'logging', label: 'Distributed Logging', lines: ['Distributed', 'Logging'],
    x: 716, y: 500,  // 126°
    color: '#fbbf24', cluster: 'Infra & Scale',       difficulty: 'Hard',   emoji: '📋',
    concepts: ['ELK Stack', 'Log Aggregation', 'Kafka', 'Time-series Indexing'],
  },
  {
    id: 'whatsapp', label: 'WhatsApp', lines: ['WhatsApp'],
    x: 577, y: 601,  // 162°
    color: '#34d399', cluster: 'Communication',       difficulty: 'Hard',   emoji: '💬',
    concepts: ['WebSockets', 'Message Queue', 'E2E Encryption', 'Delivery Status'],
  },
  {
    id: 'notification', label: 'Notification System', lines: ['Notification', 'System'],
    x: 403, y: 601,  // 198°
    color: '#34d399', cluster: 'Communication',       difficulty: 'Medium', emoji: '🔔',
    concepts: ['Push/Pull', 'APNs / FCM', 'Fan-out', 'Retry & Dead Letter'],
  },
  {
    id: 'airbnb', label: 'Airbnb', lines: ['Airbnb'],
    x: 264, y: 500,  // 234°
    color: '#f87171', cluster: 'Search & Marketplace', difficulty: 'Medium', emoji: '🏠',
    concepts: ['Geo Search', 'Availability Grid', 'Booking Flow', 'Idempotency'],
  },
  {
    id: 'search', label: 'Search Systems', lines: ['Search', 'Systems'],
    x: 210, y: 335,  // 270°
    color: '#f87171', cluster: 'Search & Marketplace', difficulty: 'Hard',   emoji: '🔍',
    concepts: ['Inverted Index', 'Typeahead', 'Elasticsearch', 'Ranking Signals'],
  },
  {
    id: 'robinhood', label: 'Robinhood', lines: ['Robinhood'],
    x: 264, y: 170,  // 306°
    color: '#fcd34d', cluster: 'Finance & Trading',   difficulty: 'Hard',   emoji: '📈',
    concepts: ['Order Book', 'Real-time Feeds', 'Strong Consistency', 'ACID Transactions'],
  },
];

// Same-cluster / concept-sharing edges
const CONCEPT_EDGES = [
  ['tinyurl',   'ratelimiter'],
  ['ratelimiter','logging'],
  ['whatsapp',  'notification'],
  ['search',    'airbnb'],
  ['instagram', 'youtube'],
  ['instagram', 'notification'],
];

const DIFF_COLOR = { Easy: '#34d399', Medium: '#fbbf24', Hard: '#f87171' };

// Cluster arc data (center angle, span, color) for background sectors
const CLUSTER_ARCS = [
  { startDeg: 330, endDeg: 30,  color: '#a78bfa' }, // Social (instagram+youtube)
  { startDeg: 30,  endDeg: 150, color: '#fbbf24' }, // Infra (tinyurl+rate+logging)
  { startDeg: 150, endDeg: 210, color: '#34d399' }, // Communication
  { startDeg: 210, endDeg: 300, color: '#f87171' }, // Search+Marketplace
  { startDeg: 300, endDeg: 330, color: '#fcd34d' }, // Finance
];

function degToRad(d) { return d * Math.PI / 180; }

function arcPath(cx, cy, r, startDeg, endDeg) {
  const s = degToRad(startDeg - 90); // -90 so 0° = top
  const e = degToRad(endDeg   - 90);
  const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
  const large = (endDeg - startDeg + 360) % 360 > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

function quadPath(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const dx = x2 - x1, dy = y2 - y1;
  return `M ${x1} ${y1} Q ${mx + dy * 0.18} ${my - dx * 0.18} ${x2} ${y2}`;
}

// ── Node card component ───────────────────────────
const ProblemNode = ({ p, isHovered, isDimmed, onEnter, onLeave }) => {
  const nW = 165, nH = p.lines.length > 1 ? 82 : 60;

  return (
    <g
      transform={`translate(${p.x}, ${p.y})`}
      style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
      opacity={isDimmed ? 0.2 : 1}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* Outer glow on hover */}
      {isHovered && (
        <rect x={-nW/2-8} y={-nH/2-8} width={nW+16} height={nH+16}
          rx={20} fill={p.color} opacity={0.15} filter="url(#nodeGlow)" />
      )}
      {/* Card fill: subtle color on hover */}
      <rect x={-nW/2} y={-nH/2} width={nW} height={nH} rx={14}
        fill={isHovered ? p.color + '22' : 'rgba(10,12,26,0.92)'}
        stroke={p.color}
        strokeWidth={isHovered ? 2 : 1}
        strokeOpacity={isHovered ? 1 : 0.55}
        style={{ transition: 'fill 0.25s, stroke-opacity 0.25s' }}
      />
      {/* Left stripe */}
      <rect x={-nW/2} y={-nH/2+7} width={4} height={nH-14}
        rx={2} fill={p.color} />
      {/* Emoji */}
      <text x={-nW/2 + 22} y={5} textAnchor="middle"
        fontSize={18} dominantBaseline="middle">
        {p.emoji}
      </text>
      {/* Label */}
      {p.lines.map((line, i) => {
        const off = p.lines.length === 1 ? 5 : (i - 0.5) * 21;
        return (
          <text key={i} x={12} y={off + 5}
            textAnchor="middle" fill="white"
            fontSize={15} fontWeight={700}
            fontFamily="'Inter', system-ui, sans-serif">
            {line}
          </text>
        );
      })}
      {/* Difficulty */}
      <text x={nW/2 - 10} y={nH/2 - 8} textAnchor="end"
        fill={DIFF_COLOR[p.difficulty]} fontSize={10} fontWeight={700}
        fontFamily="'Inter', system-ui, sans-serif" opacity={0.9}>
        {p.difficulty.toUpperCase()}
      </text>
    </g>
  );
};

// ── Main component ────────────────────────────────
const DesignGraph = () => {
  const [hovered, setHovered] = useState(null);

  const tx = TEMPLATE.x, ty = TEMPLATE.y;

  const relatedIds = hovered
    ? new Set([hovered.id,
        ...CONCEPT_EDGES
          .filter(([a, b]) => a === hovered.id || b === hovered.id)
          .flatMap(([a, b]) => [a, b])])
    : null;

  return (
    <section className="design-graph-section" id="blueprint">
      <div className="section-header">
        <div className="section-badge">The Blueprint</div>
        <h2 className="section-title">
          One Template.{' '}
          <span className="dg-gradient-text">Every Problem.</span>
        </h2>
        <p className="section-subtitle">
          Master one universal framework — then apply it to any system design
          problem, from WhatsApp to Wall Street
        </p>
      </div>

      <div className="dg-wrapper">
        {/* ── Graph ── */}
        <div className="dg-graph-area">
          <svg viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            className="dg-svg">

            <defs>
              <filter id="nodeGlow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="8" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
              <filter id="centerGlow" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="20" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
              <radialGradient id="bgGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#c0ff00" stopOpacity="0.07"/>
                <stop offset="100%" stopColor="#0a0a0f" stopOpacity="0"/>
              </radialGradient>
              {PROBLEMS.map(p => (
                <linearGradient key={p.id} id={`eg-${p.id}`}
                  gradientUnits="userSpaceOnUse"
                  x1={tx} y1={ty} x2={p.x} y2={p.y}>
                  <stop offset="0%" stopColor="#c0ff00" stopOpacity="0.6"/>
                  <stop offset="100%" stopColor={p.color} stopOpacity="0.7"/>
                </linearGradient>
              ))}
            </defs>

            {/* ── Cluster arc backgrounds ── */}
            {CLUSTER_ARCS.map((arc, i) => (
              <path key={i}
                d={arcPath(tx, ty, R + 55, arc.startDeg, arc.endDeg)}
                fill={arc.color} opacity={0.04}
              />
            ))}

            {/* Outer orbit ring */}
            <circle cx={tx} cy={ty} r={R + 55}
              fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={1}/>

            {/* Background glow */}
            <ellipse cx={tx} cy={ty} rx={300} ry={240} fill="url(#bgGlow)"/>

            {/* ── Pulse rings ── */}
            <circle cx={tx} cy={ty} r={110}
              fill="none" stroke="rgba(192,255,0,0.06)" strokeWidth={1}
              className="dg-pulse-ring dg-pulse-1"/>
            <circle cx={tx} cy={ty} r={170}
              fill="none" stroke="rgba(192,255,0,0.04)" strokeWidth={1}
              className="dg-pulse-ring dg-pulse-2"/>

            {/* ── Spoke edges ── */}
            {PROBLEMS.map(p => {
              const active = relatedIds?.has(p.id);
              const dimmed = relatedIds && !relatedIds.has(p.id);
              return (
                <path key={`s-${p.id}`}
                  d={quadPath(tx, ty, p.x, p.y)}
                  stroke={active ? `url(#eg-${p.id})` : 'rgba(255,255,255,0.07)'}
                  strokeWidth={active ? 2 : 1}
                  strokeDasharray={active ? '0' : '5 8'}
                  fill="none"
                  opacity={dimmed ? 0.12 : 1}
                  style={{ transition: 'all 0.35s' }}
                  className={active ? 'dg-spoke-active' : ''}
                />
              );
            })}

            {/* ── Concept edges ── */}
            {CONCEPT_EDGES.map(([aId, bId], i) => {
              const a = PROBLEMS.find(p => p.id === aId);
              const b = PROBLEMS.find(p => p.id === bId);
              const active = relatedIds && (relatedIds.has(aId) || relatedIds.has(bId));
              return (
                <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={active ? a.color : 'rgba(255,255,255,0.05)'}
                  strokeWidth={active ? 1.5 : 0.8}
                  strokeDasharray="3 7"
                  opacity={relatedIds && !active ? 0.08 : active ? 0.8 : 1}
                  style={{ transition: 'all 0.35s' }}
                />
              );
            })}

            {/* ── Problem nodes ── */}
            {PROBLEMS.map(p => (
              <ProblemNode key={p.id} p={p}
                isHovered={hovered?.id === p.id}
                isDimmed={relatedIds !== null && !relatedIds.has(p.id)}
                onEnter={() => setHovered(p)}
                onLeave={() => setHovered(null)}
              />
            ))}

            {/* ── Center node ── */}
            <g className="dg-center-node">
              {/* Rotating dashed ring */}
              <circle cx={tx} cy={ty} r={112}
                fill="none" stroke="rgba(192,255,0,0.18)"
                strokeWidth={1} strokeDasharray="12 8"
                className="dg-rotate-ring"/>
              {/* Static outer ring */}
              <circle cx={tx} cy={ty} r={104}
                fill="none" stroke="rgba(192,255,0,0.08)" strokeWidth={1}/>
              {/* Main circle */}
              <circle cx={tx} cy={ty} r={90}
                fill="rgba(8,12,28,0.98)"
                stroke="#c0ff00" strokeWidth={2.5}
                filter="url(#centerGlow)"/>
              {/* Inner accent */}
              <circle cx={tx} cy={ty} r={85}
                fill="none" stroke="rgba(192,255,0,0.15)" strokeWidth={1}/>
              {/* "THE" */}
              <text x={tx} y={ty-38} textAnchor="middle"
                fill="#c0ff00" fontSize={11} fontWeight={700}
                letterSpacing={3}
                fontFamily="'Courier New', monospace">
                THE
              </text>
              {/* Title */}
              <text x={tx} y={ty-16} textAnchor="middle"
                fill="white" fontSize={17} fontWeight={800}
                fontFamily="'Inter', system-ui, sans-serif">
                Universal
              </text>
              <text x={tx} y={ty+7} textAnchor="middle"
                fill="white" fontSize={17} fontWeight={800}
                fontFamily="'Inter', system-ui, sans-serif">
                Template
              </text>
              {/* Divider */}
              <line x1={tx-46} y1={ty+18} x2={tx+46} y2={ty+18}
                stroke="rgba(192,255,0,0.25)" strokeWidth={1}/>
              {/* Step count */}
              <text x={tx} y={ty+34} textAnchor="middle"
                fill="rgba(192,255,0,0.7)" fontSize={10}
                fontFamily="'Inter', system-ui, sans-serif" fontWeight={500}>
                5-Step Framework
              </text>
              {/* 5 dots */}
              {[0,1,2,3,4].map(i => (
                <circle key={i} cx={tx - 16 + i*8} cy={ty+48}
                  r={2.5} fill="rgba(192,255,0,0.5)"/>
              ))}
            </g>
          </svg>
        </div>

        {/* ── Side panel ── */}
        <div className="dg-side-panel">
          {hovered ? (
            <div className="dg-info-panel" style={{ borderColor: hovered.color + '70' }}>
              <div className="dg-panel-cluster" style={{ color: hovered.color }}>
                {hovered.emoji}&nbsp; {hovered.cluster}
              </div>
              <div className="dg-panel-title">{hovered.label}</div>
              <div className="dg-panel-diff">
                <span className="dg-diff-badge" style={{
                  color: DIFF_COLOR[hovered.difficulty],
                  background: DIFF_COLOR[hovered.difficulty] + '18',
                  borderColor: DIFF_COLOR[hovered.difficulty] + '55',
                }}>
                  {hovered.difficulty}
                </span>
                <span className="dg-panel-diff-label">Difficulty</span>
              </div>
              <div className="dg-panel-section-label">Key Concepts</div>
              <div className="dg-concepts">
                {hovered.concepts.map(c => (
                  <span key={c} className="dg-concept-tag"
                    style={{ borderColor: hovered.color + '55', color: hovered.color }}>
                    {c}
                  </span>
                ))}
              </div>
              <div className="dg-panel-section-label" style={{ marginTop: '1rem' }}>
                Template Applied
              </div>
              <div className="dg-steps">
                {TEMPLATE.steps.map((s, i) => (
                  <div key={i} className="dg-step-row">
                    <span className="dg-step-num">{i + 1}</span>
                    <span className="dg-step-label">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dg-legend">
              <div className="dg-legend-title">Hover any node<br/>to explore</div>
              <div className="dg-legend-hint">↖ See key concepts &amp; template steps</div>
              <div className="dg-legend-divider"/>
              {[
                { label: 'Social & Feed',        color: '#a78bfa' },
                { label: 'Streaming & Media',     color: '#60a5fa' },
                { label: 'Infra & Scale',          color: '#fbbf24' },
                { label: 'Communication',          color: '#34d399' },
                { label: 'Search & Marketplace',   color: '#f87171' },
                { label: 'Finance & Trading',      color: '#fcd34d' },
              ].map(({ label, color }) => (
                <div key={label} className="dg-legend-item">
                  <span className="dg-legend-dot" style={{ background: color }}/>
                  <span className="dg-legend-label">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DesignGraph;
